/**
 * Rocket radar, Profile page (2026-09-08 owner ask): the current Team GO
 * Rocket lineups read against the trainer's Shiny Dex.
 *
 * "Which shadow can I catch right now, and is its shiny still missing?"
 * Giovanni and the three leaders head the block, each with the shadow they
 * hand over: a gap tile when its shiny is out and missing (tap to choose,
 * one button commits), a checked sprite when it is already shiny in the
 * Nexus Dex, a plain sprite when GO has never released that shiny. Below
 * them the grunt pool as ONE strip of the missing shinies, each tile carrying
 * its grunt under it as a stack: type disc, then Female / Male, then Grunt
 * (owner direction 2026-09-08, second pass: cells next to each other, the
 * identifier under them). Linked to the Shiny Dex and nothing else. Grunts
 * fully hunted fold into one line.
 *
 * Data: GET /api/events/rocket (LeekDuck via ScrapedDuck, the raid roster's
 * upstream), so it stays current without a build; the brain is
 * lib/rocketRadar.ts (pure). Attribution to LeekDuck is a licence term, not
 * chrome. Sits just above PvP season; the Shiny Hunt block at the top of
 * the spine links down to it.
 */
import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { ArrowRight, Radar } from "lucide-react";
import { InfoBlock } from "@/components/InfoBlock";
import { TypeIcon } from "@/components/TypeIcon";
import { ShinyGapTiles, type ShinyTileTarget } from "@/components/ShinyGapTiles";
import { ROCKET_RADAR_DOM_ID } from "@/components/ShinyHuntBlock";
import { bindDexSyncApiFetch, getDexEntryState } from "@/lib/dexStateStore";
import { apiRequestRaw } from "@/lib/queryClient";
import { isEventFormAvailable } from "@/lib/eventFormAvailability";
import { isVariantAvailable } from "@/lib/variantAvailability";
import { isPokeType, type PokeType } from "@/lib/types";
import {
  rocketGruntLabel,
  rocketGruntLineups,
  rocketLeaderLineups,
  rocketLineupEncounters,
  type RocketLineupDto,
} from "@/lib/rocketRadar";

interface RocketResponse {
  lineups?: RocketLineupDto[];
  attribution?: { source: string; sourceUrl: string; feed: string; feedUrl: string };
  meta?: { fetchedAt?: string | null };
}

const DEX = {
  isPresent: (id: number, formId?: string) => getDexEntryState("shiny", id, formId ?? null)?.state === "present",
  isAvailable: (id: number, formId?: string) =>
    formId ? isEventFormAvailable("shiny", formId, id) : isVariantAvailable("shiny", id),
};

/** Under a leader's tile: the name, then Boss / Leader. */
function LeaderCaption({ lineup }: { lineup: RocketLineupDto }) {
  return (
    <span className="flex flex-col items-center">
      <span className="truncate w-full" style={{ color: "var(--text-secondary)" }}>{lineup.name}</span>
      <span>{lineup.kind === "boss" ? "Boss" : "Leader"}</span>
    </span>
  );
}

/** Under a grunt's tile: the type disc, then Female / Male, then Grunt. */
function GruntCaption({ lineup }: { lineup: RocketLineupDto }) {
  const label = rocketGruntLabel(lineup);
  const typed = !!lineup.type && isPokeType(lineup.type);
  return (
    <span className="flex flex-col items-center gap-0.5">
      {typed && <TypeIcon type={lineup.type as PokeType} size={14} />}
      {typed ? (
        <>
          <span style={{ color: "var(--text-secondary)" }}>{label}</span>
          <span>Grunt</span>
        </>
      ) : (
        <span className="truncate w-full">{label}</span>
      )}
    </span>
  );
}

export function RocketRadarBlock() {
  const [, setLocation] = useLocation();
  const rocketQ = useQuery<RocketResponse>({
    queryKey: ["/api/events/rocket"],
    staleTime: 5 * 60_000,
    refetchInterval: 30 * 60_000,
  });
  useEffect(() => {
    bindDexSyncApiFetch(apiRequestRaw);
  }, []);

  // FROZEN per fetch (the Quick-update rule): a committed shiny stops being
  // a gap, and a live recompute would delete the tile just added.
  const listKey = rocketQ.data?.meta?.fetchedAt ?? (rocketQ.data ? "rocket" : "");
  const { leaderTargets, leaderCaptions, leaderCount, gruntTargets, gruntCaptions, huntedGrunts } = useMemo(() => {
    const lineups = rocketQ.data?.lineups ?? [];
    // Giovanni and the leaders as ONE strip, each tile captioned with who
    // hands it over: four rows of one tile each read as a list, a row of
    // four reads as the picture it is.
    const leaderTargets: ShinyTileTarget[] = [];
    const leaderCaptions = new Map<string, RocketLineupDto>();
    const leaderLineups = rocketLeaderLineups(lineups);
    for (const lineup of leaderLineups) {
      for (const e of rocketLineupEncounters(lineup, DEX)) {
        const key = `${e.speciesId}:${e.formId ?? ""}`;
        if (leaderCaptions.has(key)) continue;
        leaderCaptions.set(key, lineup);
        leaderTargets.push({ speciesId: e.speciesId, ...(e.formId ? { formId: e.formId } : {}) });
      }
    }
    // The grunt pool as one strip of MISSING shinies; a species that walks
    // with several grunts (Gligar: Ground and Flying) is one Shiny Dex row,
    // so one tile, captioned with the first grunt that hands it over.
    const gruntTargets: ShinyTileTarget[] = [];
    const gruntCaptions = new Map<string, RocketLineupDto>();
    let huntedGrunts = 0;
    for (const lineup of rocketGruntLineups(lineups)) {
      const gaps = rocketLineupEncounters(lineup, DEX).filter((e) => e.status === "gap");
      if (gaps.length === 0) {
        huntedGrunts += 1;
        continue;
      }
      for (const e of gaps) {
        const key = `${e.speciesId}:${e.formId ?? ""}`;
        if (gruntCaptions.has(key)) continue;
        gruntCaptions.set(key, lineup);
        gruntTargets.push({ speciesId: e.speciesId, ...(e.formId ? { formId: e.formId } : {}) });
      }
    }
    return { leaderTargets, leaderCaptions, leaderCount: leaderLineups.length, gruntTargets, gruntCaptions, huntedGrunts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey]);

  const empty = leaderCount === 0 && gruntTargets.length === 0 && huntedGrunts === 0;

  return (
    <InfoBlock
      icon={Radar}
      title="Rocket radar"
      subtitle="Still missing shiny · tap to choose"
      id={ROCKET_RADAR_DOM_ID}
      data-testid="profile-rocket-radar"
    >
      {empty ? (
        <div className="rounded-2xl p-4 surface-elev text-center" data-testid="rocket-radar-empty">
          <p className="text-[11px] text-muted-foreground">
            {rocketQ.isLoading ? "Checking the Rocket lineups." : "Couldn't reach the Rocket lineups. Pull down to try again."}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {leaderCount > 0 && (
            <div data-testid="rocket-radar-leaders">
              <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)] mb-1.5">
                Giovanni and the leaders
              </p>
              <ShinyGapTiles
                listKey={`${listKey}:leaders`}
                targets={leaderTargets}
                heading={false}
                captionFor={(t) => {
                  const lineup = leaderCaptions.get(`${t.speciesId}:${t.formId ?? ""}`);
                  return lineup ? <LeaderCaption lineup={lineup} /> : null;
                }}
                testIdPrefix="rocket-radar-leaders"
                emptyLine="No leader encounters listed yet."
              />
            </div>
          )}

          <div data-testid="rocket-radar-grunts">
            <p className="text-[9px] font-bold uppercase tracking-[0.16em] text-[var(--text-tertiary)] mb-1.5">
              Grunt pool
            </p>
            {gruntTargets.length === 0 ? (
              <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }} data-testid="rocket-radar-grunts-done">
                Every grunt shadow that can be shiny already is in your Shiny Dex.
              </p>
            ) : (
              <ShinyGapTiles
                listKey={`${listKey}:grunts`}
                targets={gruntTargets}
                heading={false}
                gapsOnly
                captionFor={(t) => {
                  const lineup = gruntCaptions.get(`${t.speciesId}:${t.formId ?? ""}`);
                  return lineup ? <GruntCaption lineup={lineup} /> : null;
                }}
                testIdPrefix="rocket-radar-grunts"
              />
            )}
            {huntedGrunts > 0 && gruntTargets.length > 0 && (
              <p className="mt-2 text-[10px]" style={{ color: "var(--text-tertiary)" }} data-testid="rocket-radar-grunts-hunted">
                {huntedGrunts} more grunt{huntedGrunts === 1 ? "" : "s"} with nothing shiny left to hunt.
              </p>
            )}
          </div>

          {/* The full picture lives on its own page (2026-09-15): every
              line-up, the trainer's best three against each, and the dex
              rows this rotation's shadows could fill. */}
          <button
            type="button"
            onClick={() => setLocation("/rocket")}
            className="inline-flex items-center gap-1 text-[11px] font-bold"
            style={{ color: "var(--brand-green-on-light)", minHeight: 32 }}
            data-testid="rocket-radar-page"
          >
            Every line-up and your best three
            <ArrowRight className="w-3 h-3" aria-hidden />
          </button>

        </div>
      )}
    </InfoBlock>
  );
}
